import { useState } from "react";
import { Link } from "wouter";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowLeft, RefreshCw, Pause, Play, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMailboxHealth, useBlastMutations, type MailboxHealthRow } from "@/hooks/useBlast";
import { useToast } from "@/hooks/use-toast";

const GRADE_STYLE: Record<string, string> = {
  A: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
  B: "bg-lime-100 text-lime-700 border-lime-300 dark:bg-lime-900/30 dark:text-lime-400 dark:border-lime-800",
  C: "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
  D: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
  new: "bg-muted text-muted-foreground",
};

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(n >= 0.1 ? 0 : 1)}%`;
}

function GradeBadge({ health }: { health: MailboxHealthRow["health"] }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold cursor-default", GRADE_STYLE[health.grade])}>
          {health.grade === "new" ? "New" : health.grade}
          {health.score !== null && <span className="font-normal tabular-nums">{health.score}</span>}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <ul className="text-xs space-y-0.5">
          {health.factors.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}

function MailboxRow({ mb }: { mb: MailboxHealthRow }) {
  const { updateMailbox } = useBlastMutations();
  const { toast } = useToast();
  const toggle = () =>
    updateMailbox.mutate(
      { id: mb.id, isActive: !mb.isActive },
      {
        onSuccess: () => toast({ title: mb.isActive ? `${mb.fromAddress} paused` : `${mb.fromAddress} back in rotation` }),
        onError: (e: any) => toast({ title: "Update failed", description: String(e?.message ?? e), variant: "destructive" }),
      }
    );

  return (
    <tr className={cn("border-t text-sm", !mb.isActive && "bg-red-50/40 dark:bg-red-950/10")}>
      <td className="px-3 py-2.5">
        <p className="font-medium">{mb.fromAddress}</p>
        <p className="text-xs text-muted-foreground">{mb.displayName}</p>
      </td>
      <td className="px-3 py-2.5"><GradeBadge health={mb.health} /></td>
      <td className="px-3 py-2.5 text-xs">
        {mb.warmup.active
          ? <>Day {mb.warmup.day}/{mb.warmup.totalDays} <span className="text-muted-foreground">· {mb.warmup.cap}/day</span></>
          : <span className="text-muted-foreground">Graduated · {mb.warmup.targetCap}/day</span>}
      </td>
      <td className="px-3 py-2.5 tabular-nums">{mb.sentToday}<span className="text-muted-foreground text-xs">/{mb.capToday}</span></td>
      <td className="px-3 py-2.5 tabular-nums">{mb.sent7d}</td>
      <td className="px-3 py-2.5 tabular-nums">{mb.sentTotal.toLocaleString()}</td>
      <td className={cn("px-3 py-2.5 tabular-nums", mb.bounceRate7d > 0.05 ? "text-red-600 font-medium dark:text-red-400" : mb.bounces7d > 0 ? "text-yellow-700 dark:text-yellow-400" : "")}>
        {mb.bounces7d}{mb.sent7d > 0 && <span className="text-xs text-muted-foreground"> ({pct(mb.bounceRate7d)})</span>}
      </td>
      <td className={cn("px-3 py-2.5 tabular-nums", mb.replies7d > 0 && "text-blue-600 font-medium dark:text-blue-400")}>{mb.replies7d}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground">{relTime(mb.lastSendAt)}</td>
      <td className="px-3 py-2.5">
        {mb.isActive ? (
          <Badge variant="outline" className="text-[10px] bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">Active</Badge>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-300 cursor-default dark:bg-red-900/30 dark:text-red-400 dark:border-red-800">Paused</Badge>
            </TooltipTrigger>
            {mb.pausedReason && <TooltipContent side="left" className="max-w-xs text-xs">{mb.pausedReason}</TooltipContent>}
          </Tooltip>
        )}
      </td>
      <td className="px-2 py-2.5">
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={toggle} disabled={updateMailbox.isPending}
          title={mb.isActive ? "Pause this mailbox" : "Resume sending"}>
          {mb.isActive ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        </Button>
      </td>
    </tr>
  );
}

export default function MailboxHealth() {
  const { data: rows = [], isLoading } = useMailboxHealth();
  const { scanMailboxesNow } = useBlastMutations();
  const { toast } = useToast();
  const [scanning, setScanning] = useState(false);

  const active = rows.filter((r) => r.isActive);
  const sentToday = rows.reduce((s, r) => s + r.sentToday, 0);
  const capToday = active.reduce((s, r) => s + r.capToday, 0);
  const sent7d = rows.reduce((s, r) => s + r.sent7d, 0);
  const bounces7d = rows.reduce((s, r) => s + r.bounces7d, 0);
  const replies7d = rows.reduce((s, r) => s + r.replies7d, 0);
  const scanIssues = rows.filter((r) => r.lastScanError).length;
  const neverScanned = rows.length > 0 && rows.every((r) => !r.lastScanAt);

  const domains = [...new Set(rows.map((r) => r.domain))];

  const runScan = () => {
    setScanning(true);
    scanMailboxesNow.mutate(undefined, {
      onSuccess: () => toast({ title: "Inbox scan complete" }),
      onError: (e: any) => toast({ title: "Scan failed", description: String(e?.message ?? e), variant: "destructive" }),
      onSettled: () => setScanning(false),
    });
  };

  const stat = (value: string | number, label: string, tone = "") => (
    <div className="flex-1 min-w-[120px] bg-card border rounded-lg px-4 py-3">
      <p className={cn("text-xl font-semibold tabular-nums", tone)}>{value}</p>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mt-0.5">{label}</p>
    </div>
  );

  return (
    <AppLayout title="Mailbox Health">
      <div className="space-y-4 page-enter">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link href="/campaigns">
              <Button variant="ghost" size="icon" className="h-8 w-8"><ArrowLeft className="w-4 h-4" /></Button>
            </Link>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Mailbox Health</h1>
              <p className="text-xs text-muted-foreground">Sender reputation and volume across the outreach fleet</p>
            </div>
          </div>
          <Button variant="outline" className="gap-1.5" onClick={runScan} disabled={scanning}>
            <RefreshCw className={cn("w-3.5 h-3.5", scanning && "animate-spin")} />
            {scanning ? "Scanning inboxes…" : "Scan inboxes now"}
          </Button>
        </div>

        {rows.length > 0 && (
          <div className="flex gap-3 flex-wrap">
            {stat(`${active.length}/${rows.length}`, "Active mailboxes", active.length < rows.length ? "text-yellow-700 dark:text-yellow-400" : "text-green-600 dark:text-green-400")}
            {stat(`${sentToday}/${capToday}`, "Sent today / cap")}
            {stat(sent7d.toLocaleString(), "Sent · 7 days")}
            {stat(sent7d > 0 ? pct(bounces7d / sent7d) : "0%", "Bounce rate · 7d", bounces7d / Math.max(1, sent7d) > 0.05 ? "text-red-600 dark:text-red-400" : bounces7d > 0 ? "text-yellow-700 dark:text-yellow-400" : "text-green-600 dark:text-green-400")}
            {stat(replies7d, "Replies · 7d", replies7d > 0 ? "text-blue-600 dark:text-blue-400" : "")}
          </div>
        )}

        {neverScanned && (
          <div className="border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg px-4 py-3 text-sm dark:border-yellow-800">
            Inbox scanning hasn't run yet. Bounce and reply counts appear after the first scan completes
            (automatic every few minutes, or use "Scan inboxes now").
            {scanIssues > 0 && ` ${scanIssues} mailbox${scanIssues > 1 ? "es" : ""} reported a scan error — check Microsoft access.`}
          </div>
        )}
        {!neverScanned && scanIssues > 0 && (
          <div className="border border-red-300 bg-red-50 dark:bg-red-950/20 rounded-lg px-4 py-3 text-sm dark:border-red-800">
            {scanIssues} mailbox{scanIssues > 1 ? "es" : ""} failing inbox scan — bounce protection is blind there until fixed.
          </div>
        )}

        {isLoading ? (
          <Skeleton className="h-64" />
        ) : rows.length === 0 ? (
          <div className="text-center py-14 border rounded-lg">
            <Inbox className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="font-medium">No sending mailboxes yet</p>
          </div>
        ) : (
          domains.map((domain) => (
            <div key={domain} className="border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{domain}</div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-muted-foreground text-left">
                      <th className="px-3 py-2 font-medium">Mailbox</th>
                      <th className="px-3 py-2 font-medium">Health</th>
                      <th className="px-3 py-2 font-medium">Warmup</th>
                      <th className="px-3 py-2 font-medium">Today</th>
                      <th className="px-3 py-2 font-medium">7 days</th>
                      <th className="px-3 py-2 font-medium">Total</th>
                      <th className="px-3 py-2 font-medium">Bounces 7d</th>
                      <th className="px-3 py-2 font-medium">Replies 7d</th>
                      <th className="px-3 py-2 font-medium">Last send</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.filter((r) => r.domain === domain).map((mb) => <MailboxRow key={mb.id} mb={mb} />)}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}

        {rows.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Health grades come from the fleet's own signals: bounce rate (dominant), reply rate, and operational
            issues like pauses or scan failures. Hover a grade for its breakdown. Mailboxes auto-pause when their
            7-day bounce rate passes 8% on meaningful volume.
          </p>
        )}
      </div>
    </AppLayout>
  );
}
